import {
  BankAccountInfiniteSelectField,
  DocumentTypesInfiniteSelectField,
  FloatingFormItem,
  Group,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import CostCenterInfiniteSelectField from '@/components/_common/CostCenterInfiniteSelectField';
import CustomStatistic from '@/components/_common/CustomStatistic';
import StatusTag from '@/components/_common/StatusTag';
import { useAccountReceivableDetail } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import {
  AccountReceivableFormValues,
  AccountReceivableSituation,
  AccountReceivableSituationColor,
  AccountRecivableSituationType,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Skeleton,
  Typography,
} from 'antd';
import moment from 'moment';
import { useEffect } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { useIntl } from 'react-intl';

interface Props extends Omit<ModalProps, 'onCancel'> {
  onCancel: VoidFunction;
  installmentId?: number;
  accountReceivableId?: number;
}

export default function InstallmentReceivableViewModal({
  installmentId,
  accountReceivableId,
  ...props
}: Props) {
  const intl = useIntl();
  const [form] = Form.useForm<AccountReceivableFormValues>();

  const {
    data: accountReceivable,
    isLoading,
    dataUpdatedAt,
  } = useAccountReceivableDetail(accountReceivableId);

  const installment = accountReceivable?.installmentReceivables.find(
    (installment) => installment.id === installmentId,
  );

  const codeIdentifier = `${accountReceivable?.code} - ${installment?.installmentNumber}`;

  useEffect(() => {
    if (installment) {
      form.setFieldsValue({
        installmentNumber: installment?.installmentNumber,
        bankAccountId: installment?.bankAccountId,
        observation: installment?.observation,
        dueDate: installment?.dueDate
          ? moment(installment?.dueDate)
          : undefined,
        emissionDate: installment?.emissionDate
          ? moment(installment?.emissionDate)
          : undefined,
        payDay: installment?.payDate ? moment(installment?.payDate) : undefined,
        description: installment?.description,
        documentTypeId: installment?.documentTypeId,
        documentNumber: installment?.documentNumber,
        rateValue: installment?.rateValue,
        installmentValue: installment?.installmentValue || 0,
        discountIncrease: installment?.discountIncrease || 0,
        netValue:
          (installment?.installmentValue || 0) +
          (installment?.discountIncrease || 0) -
          (installment?.rateValue || 0),
        amountReceivable:
          (installment?.installmentValue || 0) +
          (installment?.discountIncrease || 0),
        costCenterId: accountReceivable?.costCenterId,
      });
    }
  }, [dataUpdatedAt, installment]);

  return (
    <Modal
      title={
        !!installment?.situation && (
          <Group gap={12}>
            <StatusTag
              label={AccountRecivableSituationType[installment?.situation]}
              color={
                AccountReceivableSituationColor[
                  installment?.situation ?? AccountReceivableSituation.open
                ]
              }
            />

            <Typography.Text>
              Visualização da parcela {codeIdentifier}
            </Typography.Text>
          </Group>
        )
      }
      width={1200}
      footer={[
        <Button
          type="primary"
          icon={<FaExternalLinkAlt />}
          onClick={() =>
            window.open(
              `/app/financial/billToReceiver/releaseBillToReceiver/${accountReceivableId}/installment/${installmentId}`,
              '_blank',
            )
          }
          loading={isLoading}
        >
          Editar
        </Button>,
      ]}
      {...props}
    >
      <Skeleton loading={isLoading}>
        <Form layout="vertical" form={form}>
          <Row gutter={[16, 9]}>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.description',
                })}
                name="description"
              >
                <Input disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.documentNumber',
                })}
                name="documentNumber"
              >
                <Input disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.documentType',
                })}
                name="documentTypeId"
              >
                <DocumentTypesInfiniteSelectField disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.issueDate',
                })}
                name="emissionDate"
                initialValue={moment()}
              >
                <InputDate disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.dueDate',
                })}
                name="dueDate"
              >
                <InputDate disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.payDay',
                })}
                name="payDay"
              >
                <InputDate disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={12}>
              <FloatingFormItem
                name="bankAccountId"
                label={intl.formatMessage({
                  id: 'release.form.bankAccount',
                })}
              >
                <BankAccountInfiniteSelectField
                  disabled
                  queryFilter={{ active: true }}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.value',
                })}
                name="installmentValue"
                initialValue={0}
              >
                <InputDecimal min={0.01} disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="(R$) Acres/Desc"
                name="discountIncrease"
                initialValue={0}
              >
                <InputDecimal disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.costCenter',
                })}
                name="costCenterId"
              >
                <CostCenterInfiniteSelectField disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="(R$) Tarifa"
                name="rateValue"
                initialValue={0}
              >
                <InputDecimal disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6} hidden>
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'release.form.netValue',
                })}
                name="netValue"
                initialValue={0}
              >
                <InputDecimal disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <Group gap={18}>
                <CustomStatistic
                  title="Total pago pelo cliente"
                  description="totalPagoPeloCliente = valorParcela + descontoAcrescimo"
                  value={
                    (installment?.installmentValue || 0) +
                    (installment?.discountIncrease || 0)
                  }
                  valueStyle={{ color: '#148e28' }}
                />

                <CustomStatistic
                  title="Líquido a receber"
                  description="valorLiquido = (valorParcela + descontoAcrescimo) - tarifas"
                  value={
                    (installment?.installmentValue || 0) +
                    (installment?.discountIncrease || 0) -
                    (installment?.rateValue || 0)
                  }
                  valueStyle={{ color: '#4bbacf' }}
                />
              </Group>
            </Col>
          </Row>
        </Form>
      </Skeleton>
    </Modal>
  );
}
